import React from 'react';
import { FiCpu } from 'react-icons/fi';
import { Mafs, Coordinates, Plot, Point, Text, Line } from 'mafs';
import LessonTemplate from './components/LessonTemplate';
import QuantLessonPracticeSlot from './components/QuantLessonPracticeSlot';
import { renderGreMathText as renderMathText } from '../../../../utils/renderGreMath';

const graphContainer = {
  margin: '1rem 0',
  borderRadius: '12px',
  overflow: 'hidden',
  border: '1px solid #e5e7eb',
  background: '#fff',
};

const graphTitle = {
  fontSize: '0.95rem',
  fontWeight: 600,
  color: '#374151',
  padding: '0.75rem 1rem 0.5rem',
  margin: 0,
  borderBottom: '1px solid #f3f4f6',
};

const GraphTitle = ({ children }) => (
  <h4 style={graphTitle}>{renderMathText(children)}</h4>
);

const FunctionEvaluationVisual = () => (
  <div style={graphContainer}>
    <GraphTitle>Graph of §f(x) = 3x² − 2x + 1§</GraphTitle>
    <Mafs width={500} height={350} viewBox={{ x: [-3, 3], y: [-1, 15] }}>
      <Coordinates.Cartesian
        xAxis={{ lines: 1, labels: (n) => (Number.isInteger(n) ? n : '') }}
        yAxis={{ lines: 2, labels: (n) => (Number.isInteger(n) ? n : '') }}
      />
      <Plot.OfX y={(x) => 3 * x * x - 2 * x + 1} color="#2563eb" weight={3} />
      <Point x={4} y={41} color="#ef4444" />
      <Point x={0} y={1} color="#10b981" />
      <Point x={1} y={2} color="#10b981" />
      <Text x={0.3} y={2.5} size={14} color="#10b981">
        f(0) = 1
      </Text>
      <Text x={1.5} y={3.5} size={14} color="#10b981">
        f(1) = 2
      </Text>
      <Text x={-2.2} y={14} size={14} color="#2563eb">
        f(x) = 3x² − 2x + 1
      </Text>
    </Mafs>
  </div>
);

const DomainRangeVisual = () => (
  <div style={graphContainer}>
    <GraphTitle>Domain restriction: §f(x) = 1 / (x² − 9)§</GraphTitle>
    <Mafs width={500} height={350} viewBox={{ x: [-6, 6], y: [-4, 4] }}>
      <Coordinates.Cartesian
        xAxis={{ lines: 1, labels: (n) => (Number.isInteger(n) ? n : '') }}
        yAxis={{ lines: 1, labels: (n) => (Number.isInteger(n) ? n : '') }}
      />
      <Plot.OfX
        y={(x) => {
          const d = x * x - 9;
          return Math.abs(d) < 0.15 ? NaN : 1 / d;
        }}
        color="#2563eb"
        weight={3}
      />
      {/* Vertical asymptotes */}
      <Line.Segment
        point1={[3, -4]}
        point2={[3, 4]}
        color="#f87171"
        strokeWidth={2}
      />
      <Line.Segment
        point1={[-3, -4]}
        point2={[-3, 4]}
        color="#f87171"
        strokeWidth={2}
      />
      <Text x={3.4} y={3.5} size={13} color="#ef4444">
        x = 3
      </Text>
      <Text x={-4.2} y={3.5} size={13} color="#ef4444">
        x = −3
      </Text>
      <Text x={-5} y={-3} size={13} color="#6b7280">
        Undefined at x = ±3
      </Text>
    </Mafs>
  </div>
);

const SqrtDomainVisual = () => (
  <div style={graphContainer}>
    <GraphTitle>
      Domain of §f(x) = √(2x − 6)§ — only defined for x ≥ 3
    </GraphTitle>
    <Mafs width={500} height={300} viewBox={{ x: [-1, 10], y: [-1, 5] }}>
      <Coordinates.Cartesian
        xAxis={{ lines: 1, labels: (n) => (Number.isInteger(n) ? n : '') }}
        yAxis={{ lines: 1, labels: (n) => (Number.isInteger(n) ? n : '') }}
      />
      <Plot.OfX
        y={(x) => (x < 3 ? NaN : Math.sqrt(2 * x - 6))}
        color="#2563eb"
        weight={3}
      />
      <Point x={3} y={0} color="#10b981" />
      <Text x={3.5} y={0.6} size={14} color="#10b981">
        starts at (3, 0)
      </Text>
      <Text x={7} y={3.2} size={14} color="#2563eb">
        f(x) = √(2x − 6)
      </Text>
    </Mafs>
  </div>
);

const RangeVisual = () => (
  <div style={graphContainer}>
    <GraphTitle>Range of §f(x) = x² + 4§ — minimum value is 4</GraphTitle>
    <Mafs width={500} height={350} viewBox={{ x: [-5, 5], y: [-1, 15] }}>
      <Coordinates.Cartesian
        xAxis={{ lines: 1, labels: (n) => (Number.isInteger(n) ? n : '') }}
        yAxis={{ lines: 2, labels: (n) => (Number.isInteger(n) ? n : '') }}
      />
      <Plot.OfX y={(x) => x * x + 4} color="#2563eb" weight={3} />
      <Point x={0} y={4} color="#ef4444" />
      <Line.Segment
        point1={[-5, 4]}
        point2={[5, 4]}
        color="#fca5a5"
        strokeWidth={2}
      />
      <Text x={0.6} y={5.2} size={14} color="#ef4444">
        minimum (0, 4)
      </Text>
      <Text x={3} y={13} size={14} color="#2563eb">
        f(x) = x² + 4
      </Text>
      <Text x={-4.5} y={5.5} size={13} color="#6b7280">
        Range: [4, ∞)
      </Text>
    </Mafs>
  </div>
);

const CompositionVisual = () => (
  <div style={graphContainer}>
    <GraphTitle>
      Composition: §f(x) = 2x + 1§ (blue) and §g(x) = x²§ (green)
    </GraphTitle>
    <Mafs width={500} height={350} viewBox={{ x: [-4, 4], y: [-2, 12] }}>
      <Coordinates.Cartesian
        xAxis={{ lines: 1, labels: (n) => (Number.isInteger(n) ? n : '') }}
        yAxis={{ lines: 2, labels: (n) => (Number.isInteger(n) ? n : '') }}
      />
      <Plot.OfX y={(x) => 2 * x + 1} color="#2563eb" weight={3} />
      <Plot.OfX y={(x) => x * x} color="#10b981" weight={3} />
      <Plot.OfX
        y={(x) => 2 * x * x + 1}
        color="#f59e0b"
        weight={3}
        opacity={0.8}
      />
      <Point x={3} y={9} color="#10b981" />
      <Point x={3} y={7} color="#2563eb" />
      <Text x={-3.5} y={-1} size={14} color="#2563eb">
        f(x) = 2x + 1
      </Text>
      <Text x={2.5} y={11} size={14} color="#10b981">
        g(x) = x²
      </Text>
      <Text x={-3.5} y={10} size={14} color="#f59e0b">
        f(g(x)) = 2x² + 1
      </Text>
    </Mafs>
  </div>
);

const InverseVisual = () => (
  <div style={graphContainer}>
    <GraphTitle>
      Inverse: §f(x) = 2x + 6§ (blue) and §f⁻¹(x) = (x − 6)/2§ (red)
    </GraphTitle>
    <Mafs width={500} height={400} viewBox={{ x: [-8, 10], y: [-8, 10] }}>
      <Coordinates.Cartesian
        xAxis={{ lines: 2, labels: (n) => (Number.isInteger(n) ? n : '') }}
        yAxis={{ lines: 2, labels: (n) => (Number.isInteger(n) ? n : '') }}
      />
      <Plot.OfX y={(x) => 2 * x + 6} color="#2563eb" weight={3} />
      <Plot.OfX y={(x) => (x - 6) / 2} color="#ef4444" weight={3} />
      <Plot.OfX y={(x) => x} color="#9ca3af" weight={1.5} opacity={0.6} />
      <Text x={-6} y={-4.5} size={14} color="#2563eb">
        f(x) = 2x + 6
      </Text>
      <Text x={4} y={-3} size={14} color="#ef4444">
        f⁻¹(x) = (x−6)/2
      </Text>
      <Text x={6} y={7.5} size={13} color="#9ca3af">
        y = x
      </Text>
    </Mafs>
  </div>
);

const ShiftsVisual = () => (
  <div style={graphContainer}>
    <GraphTitle>Transformations of §y = x²§</GraphTitle>
    <Mafs width={500} height={400} viewBox={{ x: [-6, 8], y: [-6, 10] }}>
      <Coordinates.Cartesian
        xAxis={{ lines: 1, labels: (n) => (Number.isInteger(n) ? n : '') }}
        yAxis={{ lines: 2, labels: (n) => (Number.isInteger(n) ? n : '') }}
      />
      <Plot.OfX y={(x) => x * x} color="#9ca3af" weight={2} opacity={0.5} />
      <Plot.OfX y={(x) => (x - 3) * (x - 3) + 2} color="#2563eb" weight={3} />
      <Point x={0} y={0} color="#9ca3af" />
      <Point x={3} y={2} color="#2563eb" />
      <Text x={-2} y={6} size={14} color="#9ca3af">
        y = x²
      </Text>
      <Text x={5.5} y={6} size={14} color="#2563eb">
        y = (x−3)² + 2
      </Text>
      <Text x={3.3} y={3.2} size={13} color="#2563eb">
        vertex (3, 2)
      </Text>
    </Mafs>
  </div>
);

const ReflectionVisual = () => (
  <div style={graphContainer}>
    <GraphTitle>
      Reflection: §f(x) = x² − 4§ (blue) vs §−f(x) = −x² + 4§ (red)
    </GraphTitle>
    <Mafs width={500} height={400} viewBox={{ x: [-5, 5], y: [-6, 6] }}>
      <Coordinates.Cartesian
        xAxis={{ lines: 1, labels: (n) => (Number.isInteger(n) ? n : '') }}
        yAxis={{ lines: 2, labels: (n) => (Number.isInteger(n) ? n : '') }}
      />
      <Plot.OfX y={(x) => x * x - 4} color="#2563eb" weight={3} />
      <Plot.OfX y={(x) => -(x * x) + 4} color="#ef4444" weight={3} />
      <Line.Segment
        point1={[-5, 0]}
        point2={[5, 0]}
        color="#d1d5db"
        strokeWidth={2}
      />
      <Point x={0} y={-4} color="#2563eb" />
      <Point x={0} y={4} color="#ef4444" />
      <Text x={3} y={4} size={14} color="#2563eb">
        f(x) = x² − 4
      </Text>
      <Text x={3} y={-3} size={14} color="#ef4444">
        −f(x) = −x² + 4
      </Text>
      <Text x={-4.5} y={0.7} size={13} color="#9ca3af">
        x-axis (mirror)
      </Text>
    </Mafs>
  </div>
);

const AbsoluteValueVisual = () => (
  <div style={graphContainer}>
    <GraphTitle>Absolute value: §y = |2x − 5|§</GraphTitle>
    <Mafs width={500} height={350} viewBox={{ x: [-2, 7], y: [-1, 10] }}>
      <Coordinates.Cartesian
        xAxis={{ lines: 1, labels: (n) => (Number.isInteger(n) ? n : '') }}
        yAxis={{ lines: 2, labels: (n) => (Number.isInteger(n) ? n : '') }}
      />
      <Plot.OfX y={(x) => Math.abs(2 * x - 5)} color="#2563eb" weight={3} />
      <Line.Segment
        point1={[-2, 9]}
        point2={[7, 9]}
        color="#fcd34d"
        strokeWidth={2}
      />
      <Point x={2.5} y={0} color="#10b981" />
      <Point x={7} y={9} color="#ef4444" />
      <Point x={-2} y={9} color="#ef4444" />
      <Text x={2.8} y={1} size={14} color="#10b981">
        vertex (2.5, 0)
      </Text>
      <Text x={4.5} y={9.8} size={13} color="#f59e0b">
        y = 9
      </Text>
      <Text x={-1.5} y={8} size={13} color="#ef4444">
        x = −2
      </Text>
      <Text x={6} y={8} size={13} color="#ef4444">
        x = 7
      </Text>
    </Mafs>
  </div>
);

const EvenOddVisual = () => (
  <div style={graphContainer}>
    <GraphTitle>
      Odd function: §f(x) = x³ + x§ — symmetric about the origin
    </GraphTitle>
    <Mafs width={500} height={400} viewBox={{ x: [-3, 3], y: [-8, 8] }}>
      <Coordinates.Cartesian
        xAxis={{ lines: 1, labels: (n) => (Number.isInteger(n) ? n : '') }}
        yAxis={{ lines: 2, labels: (n) => (Number.isInteger(n) ? n : '') }}
      />
      <Plot.OfX y={(x) => x * x * x + x} color="#2563eb" weight={3} />
      <Point x={1} y={2} color="#10b981" />
      <Point x={-1} y={-2} color="#ef4444" />
      <Line.Segment
        point1={[1, 2]}
        point2={[0, 0]}
        color="#6ee7b7"
        strokeWidth={1}
      />
      <Line.Segment
        point1={[-1, -2]}
        point2={[0, 0]}
        color="#fca5a5"
        strokeWidth={1}
      />
      <Text x={1.3} y={3} size={14} color="#10b981">
        f(1) = 2
      </Text>
      <Text x={-2.5} y={-3} size={14} color="#ef4444">
        f(−1) = −2
      </Text>
      <Text x={1.5} y={7} size={14} color="#2563eb">
        f(x) = x³ + x
      </Text>
      <Text x={-2.5} y={6} size={13} color="#6b7280">
        f(−x) = −f(x)
      </Text>
    </Mafs>
  </div>
);

const FunctionsLesson = () => {
  const sections = [
    {
      id: 'function-notation',
      title: 'Function Notation & Evaluation',
      content: {
        introduction:
          'A function is a rule that assigns exactly one output to each input. On the GRE, functions are commonly expressed as §f(x)§, and you must evaluate them by substituting values into the expression.',

        concepts: [
          {
            title: 'Function Notation',
            explanation:
              '§f(x)§ means "the value of function f at input x." It does NOT mean f multiplied by x. The letter inside the parentheses is the variable to be replaced when evaluating.',
            visual: <FunctionEvaluationVisual />,
            example: {
              problem: 'If §f(x) = 3x^2 - 2x + 1§, find §f(4)§',
              solution: [
                'Replace every §x§ with 4: §f(4) = 3(4)^2 - 2(4) + 1§',
                '§= 3(16) - 8 + 1§',
                '§= 48 - 8 + 1§',
                '§= 41§',
              ],
            },
          },
          {
            title: 'Evaluating with Expressions',
            explanation:
              'You can substitute any expression (not just numbers) into a function. Replace every instance of the variable with the given expression.',
            example: {
              problem: 'If §f(x) = x^2 + 3x§, find §f(a + 1)§',
              solution: [
                'Replace §x§ with §(a+1)§: §f(a+1) = (a+1)^2 + 3(a+1)§',
                'Expand §(a+1)^2§: §a^2 + 2a + 1§',
                'Expand §3(a+1)§: §3a + 3§',
                'Combine: §a^2 + 2a + 1 + 3a + 3 = a^2 + 5a + 4§',
              ],
            },
          },
          {
            title: 'Custom Symbol Functions',
            explanation:
              'The GRE often defines unusual symbols (♦, ★, #) as functions. These work exactly like §f(x)§ — just substitute the given values into the definition.',
            example: {
              problem:
                'For all positive integers §n§, let §n★ = n^2 - n§. What is §5★ - 3★§?',
              solution: [
                '§5★ = 5^2 - 5 = 25 - 5 = 20§',
                '§3★ = 3^2 - 3 = 9 - 3 = 6§',
                '§5★ - 3★ = 20 - 6 = 14§',
              ],
            },
          },
        ],
      },
    },
    {
      id: 'domain-range',
      title: 'Domain & Range',
      content: {
        introduction:
          'The domain of a function is the set of all valid inputs, and the range is the set of all possible outputs. GRE questions often ask you to identify values that are excluded from the domain or to determine the range.',

        concepts: [
          {
            title: 'Domain Restrictions',
            explanation:
              'Two main restrictions: (1) The denominator of a fraction cannot be zero. (2) The expression under an even root (square root, fourth root, etc.) must be non-negative.',
            visual: <DomainRangeVisual />,
            example: {
              problem: 'What is the domain of §f(x) = 1/(x^2 - 9)§?',
              solution: [
                'Set the denominator ≠ 0: §x^2 - 9 ≠ 0§',
                'Factor: §(x - 3)(x + 3) ≠ 0§',
                '§x ≠ 3§ and §x ≠ -3§',
                'Domain: all real numbers except §x = 3§ and §x = -3§',
              ],
            },
          },
          {
            title: 'Square Root Domain',
            explanation:
              'For §f(x) = \\sqrt{g(x)}§, the domain requires §g(x) ≥ 0§.',
            visual: <SqrtDomainVisual />,
            example: {
              problem: 'Find the domain of §f(x) = \\sqrt{2x - 6}§',
              solution: [
                'Require the radicand ≥ 0: §2x - 6 ≥ 0§',
                'Solve: §2x ≥ 6§ → §x ≥ 3§',
                'Domain: §[3, ∞)§',
              ],
            },
          },
          {
            title: 'Determining the Range',
            explanation:
              'The range is found by analyzing which output values the function can produce. For quadratics §f(x) = a(x-h)^2 + k§, the vertex §(h, k)§ gives the minimum or maximum.',
            visual: <RangeVisual />,
            example: {
              problem: 'What is the range of §f(x) = x^2 + 4§?',
              solution: [
                'Since §x^2 ≥ 0§ for all real §x§',
                '§x^2 + 4 ≥ 0 + 4 = 4§',
                'The minimum value of §f(x)§ is 4 (when §x = 0§)',
                'Range: §[4, ∞)§',
              ],
            },
          },
        ],
      },
    },
    {
      id: 'composition',
      title: 'Composition of Functions',
      content: {
        introduction:
          'Composing functions means applying one function to the result of another. The notation §(f ∘ g)(x)§ or §f(g(x))§ means "first apply g, then apply f to that result."',

        concepts: [
          {
            title: 'Basic Composition',
            explanation:
              '§f(g(x))§: evaluate the inner function §g(x)§ first, then plug the result into §f§.',
            example: {
              problem: 'If §f(x) = 2x + 1§ and §g(x) = x^2§, find §f(g(3))§',
              solution: [
                'Step 1 — evaluate the inner function: §g(3) = 3^2 = 9§',
                'Step 2 — plug into the outer function: §f(9) = 2(9) + 1 = 19§',
                '§f(g(3)) = 19§',
              ],
            },
          },
          {
            title: 'Algebraic Composition',
            explanation:
              'To find §f(g(x))§ as a formula, substitute the entire expression for §g(x)§ wherever §x§ appears in §f§.',
            visual: <CompositionVisual />,
            example: {
              problem:
                'If §f(x) = x + 5§ and §g(x) = 3x - 2§, find §f(g(x))§ and §g(f(x))§',
              solution: [
                '§f(g(x)) = f(3x - 2) = (3x - 2) + 5 = 3x + 3§',
                '§g(f(x)) = g(x + 5) = 3(x + 5) - 2 = 3x + 15 - 2 = 3x + 13§',
                'Note: §f(g(x)) ≠ g(f(x))§ in general — order matters!',
              ],
            },
          },
          {
            title: 'Nested Composition',
            explanation:
              'For three or more functions, work from the innermost function outward.',
            example: {
              problem:
                'If §f(x) = x + 1§, §g(x) = 2x§, and §h(x) = x^2§, find §f(g(h(2)))§',
              solution: [
                'Innermost first: §h(2) = 2^2 = 4§',
                'Then: §g(4) = 2(4) = 8§',
                'Finally: §f(8) = 8 + 1 = 9§',
                '§f(g(h(2))) = 9§',
              ],
            },
          },
        ],
      },
    },
    {
      id: 'inverse-functions',
      title: 'Inverse Functions',
      content: {
        introduction:
          'The inverse of a function §f§, written §f^{-1}§, "undoes" what §f§ does. If §f(a) = b§, then §f^{-1}(b) = a§. The GRE tests whether you can find and apply inverses.',

        concepts: [
          {
            title: 'Finding an Inverse',
            explanation:
              'To find §f^{-1}(x)§: (1) Replace §f(x)§ with §y§. (2) Swap §x§ and §y§. (3) Solve for §y§.',
            example: {
              problem: 'Find the inverse of §f(x) = 2x + 6§',
              solution: [
                'Write §y = 2x + 6§',
                'Swap: §x = 2y + 6§',
                'Solve for §y§: §x - 6 = 2y§ → §y = (x - 6)/2§',
                '§f^{-1}(x) = (x - 6)/2§',
              ],
            },
          },
          {
            title: 'Verifying Inverses',
            explanation:
              'Two functions are inverses if §f(f^{-1}(x)) = x§ and §f^{-1}(f(x)) = x§.',
            visual: <InverseVisual />,
            example: {
              problem:
                'Verify that §f(x) = 2x + 6§ and §g(x) = (x - 6)/2§ are inverses',
              solution: [
                '§f(g(x)) = f((x-6)/2) = 2 · (x-6)/2 + 6 = (x-6) + 6 = x§  ✓',
                '§g(f(x)) = g(2x+6) = ((2x+6)-6)/2 = 2x/2 = x§  ✓',
                'Both compositions yield §x§, so they are inverses.',
              ],
            },
          },
          {
            title: 'Inverse from a Table or Set of Points',
            explanation:
              'If a function is given as a set of ordered pairs, swap the §x§ and §y§ values to get the inverse.',
            example: {
              problem:
                'If §f = \\{(1,4), (2,7), (3,10)\\}§, what is §f^{-1}(7)§?',
              solution: [
                'In the original function, §f(2) = 7§',
                'Swapping input and output: §f^{-1}(7) = 2§',
              ],
            },
          },
        ],
      },
    },
    {
      id: 'transformations',
      title: 'Function Transformations',
      content: {
        introduction:
          "Understanding how changes to a function's equation affect its graph is a common GRE topic. Transformations include shifts, reflections, and stretches.",

        concepts: [
          {
            title: 'Vertical & Horizontal Shifts',
            explanation:
              '§f(x) + k§ shifts the graph up by §k§ units. §f(x) - k§ shifts it down. §f(x - h)§ shifts it right by §h§ units. §f(x + h)§ shifts it left.',
            visual: <ShiftsVisual />,
            example: {
              problem:
                'If the vertex of §y = x^2§ is at §(0, 0)§, what is the vertex of §y = (x - 3)^2 + 2§?',
              solution: [
                '§(x - 3)§ shifts the graph 3 units to the right',
                '§+ 2§ shifts the graph 2 units up',
                'New vertex: §(3, 2)§',
              ],
            },
          },
          {
            title: 'Reflections',
            explanation:
              '§-f(x)§ reflects the graph across the x-axis (flip upside down). §f(-x)§ reflects it across the y-axis (mirror left–right).',
            visual: <ReflectionVisual />,
            example: {
              problem:
                'If §f(x) = x^2 - 4§, describe the graph of §g(x) = -f(x)§',
              solution: [
                '§g(x) = -(x^2 - 4) = -x^2 + 4§',
                'The parabola opens downward instead of upward',
                'The vertex moves from §(0, -4)§ to §(0, 4)§',
              ],
            },
          },
          {
            title: 'Vertical Stretch & Compression',
            explanation:
              '§a · f(x)§ where §|a| > 1§ stretches the graph vertically, and §0 < |a| < 1§ compresses it.',
            example: {
              problem: 'Compare the graphs of §y = x^2§ and §y = 3x^2§',
              solution: [
                'At §x = 1§: §y = 1§ vs. §y = 3§',
                'At §x = 2§: §y = 4§ vs. §y = 12§',
                '§y = 3x^2§ is narrower (vertically stretched by factor 3)',
                'Both pass through the origin §(0, 0)§',
              ],
            },
          },
        ],
      },
    },
    {
      id: 'special-functions',
      title: 'Special Functions on the GRE',
      content: {
        introduction:
          'Certain function types appear frequently on the GRE: absolute value, piecewise-defined functions, and functions involving greatest integer / floor notation.',

        concepts: [
          {
            title: 'Absolute Value Functions',
            explanation:
              '§|x|§ equals §x§ when §x ≥ 0§ and §-x§ when §x < 0§. Absolute value measures the distance from zero.',
            visual: <AbsoluteValueVisual />,
            example: {
              problem: 'Solve §|2x - 5| = 9§',
              solution: [
                'Case 1: §2x - 5 = 9§ → §2x = 14§ → §x = 7§',
                'Case 2: §2x - 5 = -9§ → §2x = -4§ → §x = -2§',
                'Solutions: §x = 7§ or §x = -2§',
                'Check: §|2(7)-5| = |9| = 9§ ✓  and  §|2(-2)-5| = |-9| = 9§ ✓',
              ],
            },
          },
          {
            title: 'Piecewise Functions',
            explanation:
              'A piecewise function uses different rules for different parts of its domain. Evaluate by first determining which piece applies to the given input.',
            example: {
              problem:
                'If §f(x) = x^2§ for §x < 0§ and §f(x) = 2x + 1§ for §x ≥ 0§, find §f(-3)§ and §f(4)§',
              solution: [
                '§-3 < 0§, so use the first piece: §f(-3) = (-3)^2 = 9§',
                '§4 ≥ 0§, so use the second piece: §f(4) = 2(4) + 1 = 9§',
                'Both happen to equal 9, but through different rules',
              ],
            },
          },
          {
            title: 'Even & Odd Functions',
            explanation:
              'A function is even if §f(-x) = f(x)§ for all §x§ (symmetric about the y-axis). It is odd if §f(-x) = -f(x)§ (symmetric about the origin).',
            visual: <EvenOddVisual />,
            example: {
              problem:
                'Determine whether §f(x) = x^3 + x§ is even, odd, or neither',
              solution: [
                'Compute §f(-x) = (-x)^3 + (-x) = -x^3 - x§',
                'Compare: §-f(x) = -(x^3 + x) = -x^3 - x§',
                'Since §f(-x) = -f(x)§, the function is odd',
              ],
            },
          },
        ],
      },
    },
  ];

  const practiceQuestions = [
    {
      type: 'timed',
      timeLimit: 90,
      question: 'If §f(x) = 3x - 5§, what is §f(7)§?',
      options: ['§26§', '§21§', '§16§', '§12§', '§14§'],
      correct: 2,
      explanation: '§f(7) = 3(7) - 5 = 21 - 5 = 16§.',
    },
    {
      type: 'timed',
      timeLimit: 100,
      question: 'If §f(x) = x^2 + 2x§ and §g(x) = x - 3§, what is §f(g(5))§?',
      options: ['§12§', '§15§', '§6§', '§10§', '§8§'],
      correct: 4,
      explanation: '§g(5) = 5 - 3 = 2§. Then §f(2) = 2^2 + 2(2) = 4 + 4 = 8§.',
    },
    {
      type: 'timed',
      timeLimit: 90,
      question: 'For all integers §n§, let §n♦ = 2n^2 + n§. What is §3♦§?',
      options: ['§18§', '§21§', '§15§', '§24§', '§12§'],
      correct: 1,
      explanation: '§3♦ = 2(3)^2 + 3 = 2(9) + 3 = 18 + 3 = 21§.',
    },
    {
      type: 'timed',
      timeLimit: 120,
      question: 'What is the domain of §f(x) = \\sqrt{5 - x}§?',
      options: ['§x ≥ 5§', '§x < 5§', '§x > 0§', '§x ≤ 5§', 'All real numbers'],
      correct: 3,
      explanation: 'The radicand must be non-negative: §5 - x ≥ 0§ → §x ≤ 5§.',
    },
    {
      type: 'timed',
      timeLimit: 100,
      question: 'If §f(x) = 4x + 3§, what is §f^{-1}(x)§?',
      options: [
        '§(x - 3)/4§',
        '§4x - 3§',
        '§(x + 3)/4§',
        '§(3 - x)/4§',
        '§1/(4x + 3)§',
      ],
      correct: 0,
      explanation:
        '§y = 4x + 3§ → swap: §x = 4y + 3§ → §4y = x - 3§ → §y = (x - 3)/4§.',
    },
    {
      type: 'timed',
      timeLimit: 90,
      question: 'If §f(x) = 2x + 1§ and §g(x) = x^2§, what is §g(f(2))§?',
      options: ['§9§', '§20§', '§16§', '§36§', '§25§'],
      correct: 4,
      explanation: '§f(2) = 2(2) + 1 = 5§. Then §g(5) = 5^2 = 25§.',
    },
    {
      type: 'timed',
      timeLimit: 120,
      question:
        'The vertex of §y = x^2§ is at the origin. What is the vertex of §y = (x + 2)^2 - 5§?',
      options: [
        '§(2, -5)§',
        '§(-2, 5)§',
        '§(-2, -5)§',
        '§(2, 5)§',
        '§(-5, 2)§',
      ],
      correct: 2,
      explanation:
        '§(x + 2)§ shifts left 2 and §- 5§ shifts down 5, giving vertex §(-2, -5)§.',
    },
    {
      type: 'timed',
      timeLimit: 100,
      question: 'Solve: §|3x + 6| = 12§',
      options: [
        '§x = 2§ or §x = 6§',
        '§x = -2§ or §x = 6§',
        '§x = -2§ or §x = -6§',
        '§x = 2§ or §x = -6§',
        '§x = 6§ only',
      ],
      correct: 3,
      explanation:
        'Case 1: §3x + 6 = 12§ → §3x = 6§ → §x = 2§. Case 2: §3x + 6 = -12§ → §3x = -18§ → §x = -6§.',
    },
    {
      type: 'timed',
      timeLimit: 110,
      question:
        'If §f(x) = x^2 - 1§, what values of §x§ are NOT in the domain of §g(x) = 1/f(x)§?',
      options: [
        '§x = 0§',
        '§x = 1§ and §x = -1§',
        '§x = 1§ only',
        '§x = -1§ only',
        'All real numbers are in the domain',
      ],
      correct: 1,
      explanation:
        '§f(x) = 0§ when §x^2 - 1 = 0§ → §x = ±1§. Division by zero is undefined at these values.',
    },
    {
      type: 'timed',
      timeLimit: 90,
      question:
        'If §f(x) = x + 3§ and §g(x) = 2x§, what is §f(g(x)) - g(f(x))§?',
      options: ['§-3§', '§6§', '§3§', '§-6§', '§0§'],
      correct: 0,
      explanation:
        '§f(g(x)) = f(2x) = 2x + 3§. §g(f(x)) = g(x + 3) = 2(x + 3) = 2x + 6§. Difference: §(2x + 3) - (2x + 6) = -3§.',
    },
    {
      type: 'untimed',
      question: 'If §f(x) = 5x - 2§ and §f(a) = 13§, what is §a§?',
      options: ['§2§', '§5§', '§3§', '§11/5§', '§17/5§'],
      correct: 2,
      explanation: '§5a - 2 = 13§ → §5a = 15§ → §a = 3§.',
    },
    {
      type: 'untimed',
      question: 'What is the range of §f(x) = -x^2 + 9§?',
      options: ['§[9, ∞)§', '§(-∞, ∞)§', '§[0, 9]§', '§(-∞, 9]§', '§(-9, 9)§'],
      correct: 3,
      explanation:
        'Since §-x^2 ≤ 0§, we have §-x^2 + 9 ≤ 9§. The maximum value is 9 (at §x = 0§). Range: §(-∞, 9]§.',
    },
    {
      type: 'untimed',
      question:
        'If §f(x) = 3x + 1§ and §g(x) = x/3 - 1/3§, are §f§ and §g§ inverse functions?',
      options: [
        'No — §f(g(x)) ≠ x§',
        'No — §g(f(x)) ≠ x§',
        'Only §f(g(x)) = x§ but not the reverse',
        'Cannot be determined',
        'Yes — §f(g(x)) = x§ and §g(f(x)) = x§',
      ],
      correct: 4,
      explanation:
        '§f(g(x)) = 3(x/3 - 1/3) + 1 = x - 1 + 1 = x§. §g(f(x)) = (3x+1)/3 - 1/3 = x + 1/3 - 1/3 = x§. Both equal §x§, so yes.',
    },
    {
      type: 'untimed',
      question:
        'If §f(x) = x^2§ for §x < 2§ and §f(x) = 3x - 1§ for §x ≥ 2§, what is §f(2) + f(-1)§?',
      options: ['§5§', '§6§', '§7§', '§4§', '§8§'],
      correct: 1,
      explanation:
        '§2 ≥ 2§ → use second piece: §f(2) = 3(2) - 1 = 5§. §-1 < 2§ → use first piece: §f(-1) = (-1)^2 = 1§. Sum: §5 + 1 = 6§.',
    },
    {
      type: 'untimed',
      question:
        'The graph of §y = f(x)§ is reflected across the x-axis and shifted up 3 units. Which equation represents the result?',
      options: [
        '§y = f(-x) + 3§',
        '§y = -f(x + 3)§',
        '§y = -f(x) + 3§',
        '§y = f(x) - 3§',
        '§y = -f(x) - 3§',
      ],
      correct: 2,
      explanation:
        'Reflecting across the x-axis: §-f(x)§. Shifting up 3: add 3. Result: §y = -f(x) + 3§.',
    },
    {
      type: 'untimed',
      question: 'Is §f(x) = x^4 - 2x^2§ even, odd, or neither?',
      options: [
        'Odd — §f(-x) = -f(x)§',
        'Neither',
        'Even — §f(-x) = f(x)§',
        'Both even and odd',
        'Cannot be determined',
      ],
      correct: 2,
      explanation:
        '§f(-x) = (-x)^4 - 2(-x)^2 = x^4 - 2x^2 = f(x)§. Since §f(-x) = f(x)§, the function is even.',
    },
    {
      type: 'untimed',
      question:
        'For all real numbers §a§ and §b§, define §a ⊕ b = a^2 + ab - b^2§. What is §2 ⊕ 3§?',
      options: ['§7§', '§-1§', '§13§', '§5§', '§1§'],
      correct: 4,
      explanation: '§2 ⊕ 3 = 2^2 + (2)(3) - 3^2 = 4 + 6 - 9 = 1§.',
    },
    {
      type: 'untimed',
      question: 'If §f(x) = 2x - 1§, what is §f(f(f(1)))§?',
      options: ['§3§', '§1§', '§5§', '§7§', '§9§'],
      correct: 1,
      explanation:
        '§f(1) = 2(1) - 1 = 1§. §f(f(1)) = f(1) = 1§. §f(f(f(1))) = f(1) = 1§. (1 is a fixed point.)',
    },
    {
      type: 'timed',
      timeLimit: 100,
      question: 'If §f(x) = x^3§, what is §f(-2) + f(2)§?',
      options: ['§16§', '§-16§', '§8§', '§0§', '§-8§'],
      correct: 3,
      explanation:
        '§f(-2) = (-2)^3 = -8§. §f(2) = 2^3 = 8§. Sum: §-8 + 8 = 0§. (This also shows §f§ is odd.)',
    },
    {
      type: 'timed',
      timeLimit: 110,
      question: 'If §f(x) = x + 4§ and §f^{-1}(k) = 5§, what is §k§?',
      options: ['§9§', '§1§', '§20§', '§5§', '§-1§'],
      correct: 0,
      explanation:
        '§f^{-1}(k) = 5§ means §f(5) = k§. §f(5) = 5 + 4 = 9§. So §k = 9§.',
    },
    {
      type: 'untimed',
      questionType: 'quantitative_comparison',
      question: 'For §f(x)=2x-1§, compare the two quantities.',
      quantityA: '§f(4)§',
      quantityB: '§7§',
      correct: 2,
      explanation:
        'Compare the two quantities directly. The two quantities are equal.',
    },
    {
      type: 'untimed',
      questionType: 'multiple_choice_multi',
      question:
        'For §g(x)=x^2 - 1§, which inputs give output §8§? Select all that apply.',
      options: ['§-3§', '§-2§', '§2§', '§3§', '§4§'],
      correct: [0, 3],
      explanation: 'Solve §x^2 - 1 = 8§, so §x^2 = 9§ and §x = -3§ or §3§.',
    },
    {
      type: 'timed',
      timeLimit: 105,
      questionType: 'numeric_entry',
      question: 'If §f(x)=3x+2§, enter §f(5)§.',
      correctAnswer: '17',
      acceptedAnswers: ['17'],
      explanation: '§f(5)=3(5)+2=17§.',
    },
  ];

  const quickReference = [
    '§f(a)§: replace every §x§ in §f(x)§ with §a§',
    'Domain: exclude values that cause division by zero or negative square roots',
    '§f(g(x))§: evaluate §g(x)§ first, then apply §f§',
    'Inverse: swap §x§ and §y§, then solve for §y§',
    'Verify inverse: §f(f^{-1}(x)) = x§ and §f^{-1}(f(x)) = x§',
    '§f(x) + k§: shift up',
    '§f(x - h)§: shift right',
    '§-f(x)§: reflect across x-axis',
    '§f(-x)§: reflect across y-axis',
    'Even: §f(-x) = f(x)§',
    'Odd: §f(-x) = -f(x)§',
  ];

  const getStartedWithProps = {
    title: 'Need help with Functions?',
    spaceName: 'Quant hacks',
    humanCoachTitle: 'Talk to a GRE coach',
    liwazaAITitle: 'Ask AI',
    communityTitle: 'Ask other students',
    showHumanCoach: true,
    showLiwazaAI: true,
    showCommunity: true,
    aifirstmessage:
      'I need help understanding Functions for the GRE Quantitative section',
    aicontext:
      'Master function notation, domain & range, composition, inverse functions, and transformations',
  };

  return (
    <LessonTemplate
      lessonId="functions"
      lessonTitle="Functions"
      lessonIcon={<FiCpu />}
      lessonDescription="Master function notation, domain & range, composition, inverse functions, and transformations"
      sections={sections}
      practiceQuestions={practiceQuestions}
      quickReference={quickReference}
      getStartedWithProps={getStartedWithProps}
      supplementalPracticeSlot={
        <QuantLessonPracticeSlot lessonId="functions" />
      }
    />
  );
};

export default FunctionsLesson;
